/* eslint-disable complexity */
import React, { useState } from 'react';
import get from 'lodash/get';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getOnboardingUrl, Logger, getOnboardingStartUrl, QUERY_PARAMS } from '@futrli/shared-services';
import { toastError } from '@futrli/ui-components';

import { sendEloquaRequest } from '../../../../auth/src/utils/eloqua';
import { AuthStoreState } from '../../../../predict/src/core/types';
import { workspacesProvision } from '../../core/api';
import {
  getInvitationBusinessType,
  getInvitationInviteReference,
  getInvitationLandingPath,
  getInvitationSource,
  removeInvitationData
} from '../../../../auth/src/utils/invite';

import {
  getSage50Data,
  getSage50OrgData,
  removeSage50Data,
  removeSage50OrgData
} from '../../../../auth/src/utils/sage-50';
import { useShowIncorrectBusinessIdError } from '../hooks/use-show-incorrect-business-id-error';
import { AdditionalInfoForm } from './additional-info-form/additional-info-form';
import { useAdditionalInfoForm } from './additional-info-form/use-additional-info-form';
import { getProvisionBusinessType } from './additional-info-form/utils';
import { useRefreshCurrentUser } from '../hooks/use-refresh-current-user';
import { OnboardingNavbar } from './onboarding-navbar';

export const SageIdAdditional = () => {
  const [isLoading, setIsLoading] = useState(false);

  const formState = useAdditionalInfoForm({ businessType: getInvitationBusinessType() });
  const navigate = useNavigate();

  const {
    businessPhoneNumber,
    businessType,
    isValid,
    readPrivacy,
    receiveMarketingEmails,
    selectedCountry
  } = formState;

  const { refreshCurrentUser } = useRefreshCurrentUser();

  const submitFormDisabled = isLoading || !readPrivacy || !selectedCountry || !isValid;

  useShowIncorrectBusinessIdError();

  const { username } = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    const givenName = get(user, 'attributes.given_name', '');
    return {
      firstName: givenName,
      username: get(user, 'attributes.email', '')
    };
  });

  const handleNextClick = async () => {
    try {
      setIsLoading(true);
      const inviteReference = getInvitationInviteReference();
      const inviteSource = getInvitationSource();
      const inviteLandingPath = getInvitationLandingPath();
      const sage50BusinessId = getSage50Data();

      const { workspaceId, organisationId, datasourceId } = getSage50OrgData();

      const isInvitedUser = !!inviteReference && !!inviteSource;

      const workspaceData = await workspacesProvision(
        null,
        inviteSource,
        inviteReference,
        sage50BusinessId,
        username,
        getProvisionBusinessType(businessType)
      );

      // Get updated user after provision request and update redux
      await refreshCurrentUser();

      // If user is invited or there is sage50 integration, don't send Eloqua request
      if (!isInvitedUser && !sage50BusinessId) {
        sendEloquaRequest(selectedCountry, businessPhoneNumber, !!receiveMarketingEmails, businessType);
      }
      // Handling invite redirect
      if (isInvitedUser && inviteLandingPath) {
        removeInvitationData();
        return navigate(inviteLandingPath);
      }
      // Handling sage 50 redirect
      if (workspaceId && organisationId && datasourceId && sage50BusinessId) {
        removeSage50OrgData();
        removeSage50Data();
        return navigate(
          `${getOnboardingStartUrl()}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${
            QUERY_PARAMS.ORG_ID
          }=${organisationId}&${QUERY_PARAMS.DATASOURCE_ID}=${datasourceId}&${QUERY_PARAMS.STATUS}=SUCCESS`
        );
      }

      navigate(`${getOnboardingUrl()}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceData.id}`);
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <OnboardingNavbar />
      <AdditionalInfoForm {...formState} submitFormDisabled={submitFormDisabled} handleNextClick={handleNextClick} />
    </>
  );
};
